// PartnerUnregisteredPage — 業者未登録 一覧 (v5 Phase 4, 2026-05-20)
//   2 tab 構成:
//     Tab 1: 業者未登録一覧 (= partner_status='unregistered' / unresolved / sentinel 0)
//     Tab 2: 既登録だが紐付け未完了 (= partner_status='candidate_low_score', score 0.60-0.85)
//   各行展開: GET /api/admin/partner-resolve → 候補 dropdown
//             POST /api/admin/partner-bind {arica_rowid, partner_id, seed_curated}
//             「freee で新規作成 ↗」 deep link (R3: 自動作成 禁止、 user 手動のみ)

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Container,
  PageTitle,
  CardBase,
  HStack,
  VStack,
  Button,
  Text,
  Note,
  Breadcrumbs,
  MessageBlock,
  TabBar,
  InlineSpinner,
  TextField,
  SearchField,
  NoDataIllust,
} from '@freee_jp/vibes';
import { MdPersonOff, MdOpenInNew, MdRefresh, MdLink, MdSearch } from 'react-icons/md';

const API_BASE = 'https://invoice-review.system-0f7.workers.dev';
const FREEE_PARTNER_NEW_URL = 'https://secure.freee.co.jp/partners/new';

type Tab = 'unregistered' | 'low_score';

interface UnregisteredCandidate {
  partner_id: number;
  partner_name: string;
  score: number;
  matched_variant?: string | null;
  shortcut1?: string | null;
  code?: string | null;
}

interface StuckItem {
  ref_number: string;
  arica_rowid: number | null;
  // GATE_E で proposal 自体が作られなかった行 (source='run_item_gate_e') では null
  deal_proposal_id: number | null;
  partner_id: number | null;
  partner_name: string | null;
  issue_date: string | null;
  due_date: string | null;
  amount: number | null;
  status: string;
  partner_status: string;
  unregistered_candidates: UnregisteredCandidate[];
  v5_orchestrator_verdict: string | null;
  proposed_at: string | null;
  source?: 'deal_proposal' | 'run_item_gate_e';
  // source='run_item_gate_e' のみ: ARICA から補完した検索の手がかり
  vendor_hint?: string | null;
  note?: string | null;
  run_id?: string | null;
  email_from_address?: string | null;
}

interface PartnerResolveResult {
  ok: boolean;
  t_number: string | null;
  vendor_name: string | null;
  nta_lookup: {
    t_number?: string;
    corporate_name?: string;
    address?: string;
    kind?: string;
    source?: string;
    not_found?: boolean;
  } | null;
  candidates: UnregisteredCandidate[];
  cache_hit?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const fmtAmount = (n: number | null | undefined) =>
  n == null ? '—' : `¥${n.toLocaleString('ja-JP')}`;

const STYLES = {
  page: {
    background: 'linear-gradient(180deg, #f8f7f5 0%, #f3f1ef 100%)',
    minHeight: '100vh',
    paddingBottom: '3rem',
  } as React.CSSProperties,
  card: {
    background: '#fff',
    borderRadius: '8px',
    padding: '20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    border: '1px solid #e5e4e2',
  } as React.CSSProperties,
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.8125rem',
  } as React.CSSProperties,
  thCell: {
    padding: '11px 14px',
    fontSize: '0.6875rem',
    fontWeight: 700,
    color: '#6b6560',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.06em',
    whiteSpace: 'nowrap' as const,
    borderBottom: '1px solid #e5e4e2',
    background: 'linear-gradient(180deg, #fafaf8 0%, #f5f3f2 100%)',
    textAlign: 'left' as const,
  } as React.CSSProperties,
  tdCell: {
    padding: '12px 14px',
    verticalAlign: 'middle' as const,
    borderBottom: '1px solid #f0efed',
  } as React.CSSProperties,
  badge: (color: string) => ({
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '0.6875rem',
    fontWeight: 600,
    background: `${color}22`,
    color,
    border: `1px solid ${color}44`,
  } as React.CSSProperties),
  expandPanel: {
    padding: '16px',
    background: '#fafaf8',
    borderTop: '1px solid #e5e4e2',
    borderBottom: '1px solid #e5e4e2',
  } as React.CSSProperties,
};

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------
export const PartnerUnregisteredPage: React.FC = () => {
  const [tab, setTab] = useState<Tab>('unregistered');
  const [items, setItems] = useState<StuckItem[]>([]);
  const [counts, setCounts] = useState<{ unregistered: number; low_score: number }>({ unregistered: 0, low_score: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedRid, setExpandedRid] = useState<number | null>(null);
  const [search, setSearch] = useState('');

  const loadItems = useCallback(async (forTab: Tab = tab) => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/admin/gate-e-stuck-rids?tab=${forTab}&limit=300`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setItems(data.items || []);
      setCounts(data.counts || { unregistered: 0, low_score: 0 });
    } catch (e: any) {
      setError(e.message || 'load failed');
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { loadItems(tab); }, [tab, loadItems]);

  const filteredItems = useMemo(() => {
    if (!search.trim()) return items;
    const lower = search.trim().toLowerCase();
    return items.filter((it) =>
      (it.partner_name || '').toLowerCase().includes(lower) ||
      (it.vendor_hint || '').toLowerCase().includes(lower) ||
      String(it.arica_rowid || '').includes(lower) ||
      (it.ref_number || '').toLowerCase().includes(lower),
    );
  }, [items, search]);

  return (
    <div style={STYLES.page}>
      <Container>
        <Breadcrumbs
          links={[
            { url: '#', title: 'FREEEMAN' },
            { url: '#partner_unregistered', title: '業者未登録一覧' },
          ]}
        />
        <div style={{ marginTop: '16px', marginBottom: '12px' }}>
          <HStack gap={2} alignItems="center">
            <MdPersonOff size={28} color="#d97706" />
            <PageTitle>業者未登録一覧</PageTitle>
          </HStack>
        </div>
        <div style={{ marginBottom: '16px' }}>
          <Note>
            v5 Phase 4: 取引先未確定で proposal が GATE_E_canonical_stuck している RID を 一覧。
            T番号 / vendor_name から freee partner 候補を返却、 user 紐付けで proposal 再 build。
            freee 新規作成は手動 (R3 厳守、 自動作成禁止)。
          </Note>
        </div>

        <CardBase>
          <div style={{ padding: '16px' }}>
          <VStack gap={2}>
            <HStack gap={2} alignItems="center" justifyContent="space-between">
              <TabBar
                tabs={[
                  `業者未登録一覧 (${counts.unregistered})`,
                  `紐付け未完了 (${counts.low_score})`,
                ]}
                currentTabIndex={tab === 'unregistered' ? 0 : 1}
                onClickTab={(i: number) => setTab(i === 0 ? 'unregistered' : 'low_score')}
              />
              <HStack gap={1}>
                <SearchField
                  placeholder="vendor / rid / ref で検索"
                  value={search}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
                  width="medium"
                />
                <Button onClick={() => loadItems(tab)} disabled={loading}>
                  <MdRefresh /> 再読込
                </Button>
              </HStack>
            </HStack>

            {error && <MessageBlock success={false}>{error}</MessageBlock>}

            {loading && (
              <div style={{ padding: '24px' }}>
                <HStack gap={1} alignItems="center">
                  <InlineSpinner isLoading={true} /> <Text>読込中...</Text>
                </HStack>
              </div>
            )}

            {!loading && filteredItems.length === 0 && (
              <div style={{ padding: '32px', textAlign: 'center' }}>
                <NoDataIllust />
                <div style={{ marginTop: '12px' }}>
                  <Text>
                    {tab === 'unregistered' ? '業者未登録の RID はありません' : '紐付け未完了の RID はありません'}
                  </Text>
                </div>
              </div>
            )}

            {!loading && filteredItems.length > 0 && (
              <div style={{ overflowX: 'auto' }}>
                <table style={STYLES.table}>
                  <thead>
                    <tr>
                      <th style={STYLES.thCell}>RID</th>
                      <th style={STYLES.thCell}>vendor (PDF)</th>
                      <th style={STYLES.thCell}>amount</th>
                      <th style={STYLES.thCell}>issue_date</th>
                      <th style={STYLES.thCell}>verdict</th>
                      <th style={STYLES.thCell}>status</th>
                      <th style={STYLES.thCell}>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.map((it) => (
                      <React.Fragment key={it.ref_number}>
                        <tr>
                          <td style={STYLES.tdCell}>
                            <code>{it.arica_rowid || '—'}</code>
                            <div style={{ fontSize: '0.6875rem', color: '#888' }}>{it.ref_number}</div>
                          </td>
                          <td style={STYLES.tdCell}>
                            <Text>{it.partner_name || it.vendor_hint || '(取引先未確定)'}</Text>
                            {it.source === 'run_item_gate_e' && (
                              <div style={{ marginTop: '2px' }}>
                                <span style={STYLES.badge('#dc2626')}>proposal 未生成</span>
                              </div>
                            )}
                          </td>
                          <td style={STYLES.tdCell}>{fmtAmount(it.amount)}</td>
                          <td style={STYLES.tdCell}>{it.issue_date || '—'}</td>
                          <td style={STYLES.tdCell}>
                            {it.v5_orchestrator_verdict ? (
                              <span style={STYLES.badge('#2e7cf6')}>{it.v5_orchestrator_verdict}</span>
                            ) : '—'}
                          </td>
                          <td style={STYLES.tdCell}>
                            <span style={STYLES.badge(it.partner_status === 'unregistered' ? '#d97706' : '#6366f1')}>
                              {it.partner_status}
                            </span>
                          </td>
                          <td style={STYLES.tdCell}>
                            <Button
                              appearance="primary"
                              onClick={() => setExpandedRid(expandedRid === it.arica_rowid ? null : it.arica_rowid)}
                            >
                              {expandedRid === it.arica_rowid ? '閉じる' : '紐付け候補を確認'}
                            </Button>
                          </td>
                        </tr>
                        {expandedRid === it.arica_rowid && it.arica_rowid !== null && (
                          <tr>
                            <td colSpan={7} style={{ padding: 0 }}>
                              <ResolvePanel
                                aricaRowid={it.arica_rowid}
                                vendorName={it.partner_name || it.vendor_hint || null}
                                initialCandidates={it.unregistered_candidates}
                                onBound={() => {
                                  setExpandedRid(null);
                                  loadItems(tab);
                                }}
                              />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </VStack>
          </div>
        </CardBase>
      </Container>
    </div>
  );
};

// ---------------------------------------------------------------------------
// ResolvePanel — 各行展開時の partner 候補 dropdown + bind UI
// ---------------------------------------------------------------------------
const ResolvePanel: React.FC<{
  aricaRowid: number;
  vendorName: string | null;
  initialCandidates: UnregisteredCandidate[];
  onBound: () => void;
}> = ({ aricaRowid, vendorName, initialCandidates, onBound }) => {
  const [tNumber, setTNumber] = useState('');
  const [searchName, setSearchName] = useState(vendorName || '');
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);
  const [resolveResult, setResolveResult] = useState<PartnerResolveResult | null>(
    initialCandidates && initialCandidates.length > 0 ? {
      ok: true,
      t_number: null,
      vendor_name: vendorName,
      nta_lookup: null,
      candidates: initialCandidates,
    } : null,
  );
  const [selectedPartnerId, setSelectedPartnerId] = useState<number | null>(null);
  const [seedCurated, setSeedCurated] = useState(true);
  const [binding, setBinding] = useState(false);
  const [bindError, setBindError] = useState<string | null>(null);
  const [bindOk, setBindOk] = useState(false);
  // 紐付けが どこに永続化されたか (ARICA 正本 / FREEEMAN 側のみ) を user に見せる
  const [bindDetail, setBindDetail] = useState<string | null>(null);

  const doResolve = useCallback(async () => {
    if (!tNumber.trim() && !searchName.trim()) {
      setResolveError('T番号 または vendor_name を入力してください');
      return;
    }
    setResolving(true);
    setResolveError(null);
    try {
      const q = new URLSearchParams();
      if (tNumber.trim()) q.set('t_number', tNumber.trim());
      if (searchName.trim()) q.set('vendor_name', searchName.trim());
      const r = await fetch(`${API_BASE}/api/admin/partner-resolve?${q}`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setResolveResult(data);
      setSelectedPartnerId(null);
    } catch (e: any) {
      setResolveError(e.message || 'resolve failed');
    } finally {
      setResolving(false);
    }
  }, [tNumber, searchName]);

  const doBind = useCallback(async () => {
    if (!selectedPartnerId) {
      setBindError('partner を 選択してください');
      return;
    }
    setBinding(true);
    setBindError(null);
    setBindOk(false);
    try {
      const r = await fetch(`${API_BASE}/api/admin/partner-bind`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          arica_rowid: aricaRowid,
          partner_id: selectedPartnerId,
          seed_curated: seedCurated,
        }),
      });
      if (!r.ok) {
        const errBody = await r.json().catch(() => ({}));
        throw new Error(errBody.error || `HTTP ${r.status}`);
      }
      const data = await r.json();
      if (!data.ok) throw new Error(data.error || 'bind_failed');
      const ab = data.arica_bind || {};
      const where = [
        data.override_saved ? '確定済取引先' : null,
        data.recon_rows_updated > 0 ? 'reconciliation' : null,
      ].filter(Boolean).join(' / ');
      if (ab.applied) {
        setBindDetail(`ARICA invoices.partner_id も更新済 (送信者 ${ab.from_address || '?'} を自動解決に登録)`);
      } else if (ab.skipped_reason === 'internal_forwarder') {
        setBindDetail(
          `FREEEMAN 側で確定 (${where})。` +
          ` 社内転送メール (${ab.from_address}) のため ARICA 側は更新していません` +
          ` — 送信者を取引先として登録すると 以降の転送分が全て誤紐付けになるためです。`,
        );
      } else {
        setBindDetail(
          `FREEEMAN 側で確定 (${where})。` +
          ` ARICA 未更新: ${ab.skipped_reason || 'unknown'}${ab.hint ? ` — ${ab.hint}` : ''}`,
        );
      }
      setBindOk(true);
      setTimeout(onBound, 2600);
    } catch (e: any) {
      setBindError(e.message || 'bind failed');
    } finally {
      setBinding(false);
    }
  }, [aricaRowid, selectedPartnerId, seedCurated, onBound]);

  return (
    <div style={STYLES.expandPanel}>
      <VStack gap={2}>
        <HStack gap={2} alignItems="center">
          <MdSearch size={20} />
          <Text weight="bold">partner 候補 検索 (T番号 / vendor_name)</Text>
        </HStack>
        <HStack gap={1} alignItems="end">
          <div>
            <Text size={0.75}>T番号</Text>
            <TextField
              placeholder="T1234567890123"
              value={tNumber}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTNumber(e.target.value)}
              width="medium"
            />
          </div>
          <div>
            <Text size={0.75}>vendor_name</Text>
            <TextField
              placeholder="守屋雅章"
              value={searchName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchName(e.target.value)}
              width="medium"
            />
          </div>
          <Button onClick={doResolve} disabled={resolving}>
            {resolving ? <InlineSpinner isLoading={true} /> : <MdSearch />} 検索
          </Button>
          <a
            href={FREEE_PARTNER_NEW_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              padding: '8px 12px',
              border: '1px solid #2e7cf6',
              borderRadius: '4px',
              color: '#2e7cf6',
              textDecoration: 'none',
              fontSize: '0.8125rem',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            <MdOpenInNew /> freee で新規作成
          </a>
        </HStack>

        {resolveError && <MessageBlock success={false}>{resolveError}</MessageBlock>}

        {resolveResult && resolveResult.nta_lookup && resolveResult.nta_lookup.corporate_name && (
          <CardBase>
            <div style={{ padding: '12px' }}>
              <VStack gap={1}>
                <Text weight="bold">
                  国税庁 公表データ ({resolveResult.cache_hit ? 'cache' : 'NTA API'})
                </Text>
                <Text>法人名: {resolveResult.nta_lookup.corporate_name}</Text>
                {resolveResult.nta_lookup.address && <Text size={0.75}>住所: {resolveResult.nta_lookup.address}</Text>}
                {resolveResult.nta_lookup.kind && <Text size={0.75}>kind: {resolveResult.nta_lookup.kind}</Text>}
              </VStack>
            </div>
          </CardBase>
        )}

        {resolveResult && resolveResult.candidates && resolveResult.candidates.length > 0 && (
          <VStack gap={1}>
            <Text weight="bold">freee partner 候補 ({resolveResult.candidates.length} 件)</Text>
            <table style={STYLES.table}>
              <thead>
                <tr>
                  <th style={STYLES.thCell}>選択</th>
                  <th style={STYLES.thCell}>partner_id</th>
                  <th style={STYLES.thCell}>name</th>
                  <th style={STYLES.thCell}>score</th>
                  <th style={STYLES.thCell}>matched variant</th>
                </tr>
              </thead>
              <tbody>
                {resolveResult.candidates.map((c) => (
                  <tr key={c.partner_id} style={{ background: selectedPartnerId === c.partner_id ? '#ebf3ff' : 'transparent' }}>
                    <td style={STYLES.tdCell}>
                      <input
                        type="radio"
                        name="partner_candidate"
                        checked={selectedPartnerId === c.partner_id}
                        onChange={() => setSelectedPartnerId(c.partner_id)}
                      />
                    </td>
                    <td style={STYLES.tdCell}><code>{c.partner_id}</code></td>
                    <td style={STYLES.tdCell}>{c.partner_name}</td>
                    <td style={STYLES.tdCell}>
                      <span style={STYLES.badge(c.score >= 0.85 ? '#10b981' : c.score >= 0.60 ? '#f59e0b' : '#ef4444')}>
                        {(c.score * 100).toFixed(1)}%
                      </span>
                    </td>
                    <td style={STYLES.tdCell}>{c.matched_variant || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </VStack>
        )}

        {resolveResult && (!resolveResult.candidates || resolveResult.candidates.length === 0) && (
          <MessageBlock success={false}>
            freee partner 候補なし。 「freee で新規作成」 から手動登録 (R3 厳守)。
          </MessageBlock>
        )}

        {selectedPartnerId && (
          <HStack gap={2} alignItems="center">
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              <input type="checkbox" checked={seedCurated} onChange={(e) => setSeedCurated(e.target.checked)} />
              <Text>partner_curated_patterns に seed 行を作成</Text>
            </label>
            <Button appearance="primary" onClick={doBind} disabled={binding}>
              {binding ? <InlineSpinner isLoading={true} /> : <MdLink />} 紐付けて proposal 再 build
            </Button>
          </HStack>
        )}

        {bindError && <MessageBlock success={false}>{bindError}</MessageBlock>}
        {bindOk && (
          <MessageBlock success={true}>
            紐付け完了。 proposal を再 build しました。 一覧を再読込します...
            {bindDetail && <div style={{ marginTop: '4px', fontSize: '0.8125rem' }}>{bindDetail}</div>}
          </MessageBlock>
        )}
      </VStack>
    </div>
  );
};

export default PartnerUnregisteredPage;
